import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Plus, Search, Trash2, X } from "lucide-react";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { Spinner } from "@/shared/components/ui/spinner";
import { useDebouncedSearch } from "@/shared/hooks/useDebouncedSearch";
import { getApiErrorMessage } from "@/shared/lib/api-error";
import { productService } from "@/features/products/services/product-api.service";
import type { ProductResponseDTO } from "@/features/products/types/product.types";
import { ProductType } from "@/shared/types/enums";

export interface GownItemDraft {
  productId: number;
  quantity: number;
}

interface ProductSummary {
  id: number;
  brand: string;
  color: string;
  name?: string;
}

interface GownItemsEditorProps {
  value: GownItemDraft[];
  onChange: (items: GownItemDraft[]) => void;
  /** Resumo dos produtos já vinculados — popula os labels iniciais. */
  initialProducts?: ProductSummary[];
  disabled?: boolean;
}

const DEBOUNCE_MS = 300;
const SEARCH_PAGE_SIZE = 8;

function productLabel(product: ProductSummary): string {
  return [product.brand, product.color, product.name]
    .filter((part): part is string => Boolean(part && part.trim()))
    .join(" · ");
}

export function GownItemsEditor({
  value,
  onChange,
  initialProducts,
  disabled = false,
}: GownItemsEditorProps) {
  const [productCache, setProductCache] = useState<Map<number, ProductSummary>>(
    () => {
      const map = new Map<number, ProductSummary>();
      initialProducts?.forEach((product) => map.set(product.id, product));
      return map;
    },
  );
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [results, setResults] = useState<ProductResponseDTO[]>([]);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const lookedUpIdsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!initialProducts?.length) return;
    // Popula cache com produtos pré-carregados vindos por prop.
    // Sincronização com prop externa — legítimo.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setProductCache((prev) => {
      const next = new Map(prev);
      initialProducts.forEach((product) => {
        if (!next.has(product.id)) next.set(product.id, product);
      });
      return next;
    });
  }, [initialProducts]);

  useEffect(() => {
    const missing = value
      .map((item) => item.productId)
      .filter((id) => !productCache.has(id) && !lookedUpIdsRef.current.has(id));
    if (missing.length === 0) return;
    missing.forEach((id) => lookedUpIdsRef.current.add(id));
    const controller = new AbortController();
    Promise.all(
      missing.map((id) =>
        productService
          .getByIdUser(id, { signal: controller.signal })
          .catch(() => null),
      ),
    ).then((products) => {
      if (controller.signal.aborted) return;
      const resolved = products.filter(
        (p): p is ProductResponseDTO => p !== null,
      );
      if (resolved.length === 0) return;
      setProductCache((prev) => {
        const next = new Map(prev);
        resolved.forEach((p) =>
          next.set(p.id, {
            id: p.id,
            brand: p.brand,
            color: p.color,
            name: p.name,
          }),
        );
        return next;
      });
    });
    return () => controller.abort();
    // productCache é lido via closure ao decidir quais ids buscar; o
    // efeito não deve re-disparar quando o cache cresce — esse caso
    // fica protegido pelo lookedUpIdsRef que evita refetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const fetchProducts = useCallback(
    async (_term: string, signal: AbortSignal) => {
      const response = await productService.searchUser(
        { type: [ProductType.GOWN], size: SEARCH_PAGE_SIZE },
        { signal },
      );
      return response.content;
    },
    [],
  );

  useDebouncedSearch({
    term: searchTerm,
    enabled: searchOpen,
    fetcher: fetchProducts,
    setItems: setResults,
    setLoading: setSearching,
    isValid: () => true,
    onError: (e) => toast.error(getApiErrorMessage(e, "Erro ao buscar peças")),
    debounceMs: DEBOUNCE_MS,
  });

  useEffect(() => {
    if (!searchOpen) return;
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setSearchOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [searchOpen]);

  const handleAdd = useCallback(
    (product: ProductResponseDTO) => {
      if (value.some((item) => item.productId === product.id)) {
        setSearchOpen(false);
        setSearchTerm("");
        return;
      }
      setProductCache((prev) => {
        const next = new Map(prev);
        next.set(product.id, {
          id: product.id,
          brand: product.brand,
          color: product.color,
          name: product.name,
        });
        return next;
      });
      onChange([...value, { productId: product.id, quantity: 1 }]);
      setSearchOpen(false);
      setSearchTerm("");
    },
    [value, onChange],
  );

  const handleRemove = useCallback(
    (productId: number) => {
      onChange(value.filter((item) => item.productId !== productId));
    },
    [value, onChange],
  );

  const handleQuantityChange = useCallback(
    (productId: number, quantity: number) => {
      onChange(
        value.map((item) =>
          item.productId === productId
            ? { ...item, quantity: Math.max(1, quantity) }
            : item,
        ),
      );
    },
    [value, onChange],
  );

  const visibleResults = results.filter(
    (product) => !value.some((item) => item.productId === product.id),
  );

  return (
    <div ref={containerRef} className="space-y-2">
      {value.length > 0 && (
        <div className="space-y-2">
          {value.map((item) => {
            const product = productCache.get(item.productId);
            const label = product
              ? productLabel(product)
              : `Produto #${item.productId}`;
            return (
              <div
                key={item.productId}
                className="flex items-center gap-2 rounded-md border border-input bg-background px-3 py-2 text-sm"
              >
                <span className="flex-1 truncate">{label}</span>
                <Input
                  type="number"
                  min="1"
                  step="1"
                  value={item.quantity}
                  onChange={(e) =>
                    handleQuantityChange(item.productId, Number(e.target.value))
                  }
                  disabled={disabled}
                  aria-label={`Quantidade de ${label}`}
                  className="w-20 h-8"
                />
                <button
                  type="button"
                  onClick={() => handleRemove(item.productId)}
                  disabled={disabled}
                  aria-label={`Remover ${label}`}
                  className="rounded-full p-1 hover:bg-accent text-destructive"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="relative">
        {searchOpen ? (
          <>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" />
              <Input
                value={searchTerm}
                placeholder="Buscar produto beca…"
                onChange={(e) => setSearchTerm(e.target.value)}
                disabled={disabled}
                className="pl-9 pr-9"
                autoFocus
              />
              <button
                type="button"
                onClick={() => setSearchOpen(false)}
                aria-label="Fechar busca"
                className="absolute right-2 top-1/2 -translate-y-1/2 rounded-full p-0.5 hover:bg-accent"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="absolute top-full left-0 right-0 mt-1 bg-popover border rounded-md shadow-md max-h-60 overflow-y-auto z-50">
              {searching ? (
                <div className="flex items-center justify-center gap-2 p-3 text-sm text-muted-foreground">
                  <Spinner className="w-4 h-4" />
                  Buscando…
                </div>
              ) : visibleResults.length === 0 ? (
                <div className="p-3 text-center text-sm text-muted-foreground">
                  Nenhum produto encontrado
                </div>
              ) : (
                visibleResults.map((product) => (
                  <button
                    key={product.id}
                    type="button"
                    onClick={() => handleAdd(product)}
                    className="w-full text-left px-3 py-2 hover:bg-accent text-sm"
                  >
                    {productLabel({
                      id: product.id,
                      brand: product.brand,
                      color: product.color,
                      name: product.name,
                    })}
                  </button>
                ))
              )}
            </div>
          </>
        ) : (
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setSearchOpen(true)}
            disabled={disabled}
            className="gap-2 w-full"
          >
            <Plus className="w-4 h-4" />
            Adicionar produto
          </Button>
        )}
      </div>
    </div>
  );
}
